import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  Renderer2,
} from "@angular/core";
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import {ActivatedRoute, Router} from "@angular/router";
import {TranslateModule, TranslateService} from "@ngx-translate/core";
import {Subscription} from "rxjs";
import {CustomError} from "src/app/modules/shared/interfaces/CustomError.interface";
import {LegalAcceptanceControlsComponent} from "../../components/legal-acceptance-controls/legal-acceptance-controls.component";
import type {CurrentLegalVersionsView} from "../../interfaces/current-legal-versions.interface";
import {AuthService} from "../../services/auth.service";
import {LegalService} from "../../services/legal.service";

@Component({
  selector: "app-complete-legal-signup",
  templateUrl: "./complete-legal-signup.component.html",
  styleUrls: ["./complete-legal-signup.component.css"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    TranslateModule,
    LegalAcceptanceControlsComponent,
  ],
})
export class CompleteLegalSignupComponent implements OnInit, OnDestroy {
  public readonly legalForm: FormGroup;
  public currentLegalVersions?: CurrentLegalVersionsView;
  public error?: CustomError;
  public submitButtonText?: string;
  public versionsUnavailable = true;
  public submitting = false;

  private readonly userLanguage: "fr" | "en";
  private readonly subscriptions: Subscription[] = [];

  constructor(
    private readonly translate: TranslateService,
    private readonly formBuilder: FormBuilder,
    private readonly authService: AuthService,
    private readonly legalService: LegalService,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly cdr: ChangeDetectorRef,
    private readonly renderer: Renderer2
  ) {
    this.userLanguage = this.authService.user?.language === "fr" ? "fr" : "en";
    this.legalForm = this.formBuilder.group({
      acceptTerms: [false, Validators.requiredTrue],
      acceptPrivacy: [false, Validators.requiredTrue],
    });
  }

  ngOnInit(): void {
    this.renderer.addClass(document.body, "arts-bg");

    if (this.authService.hasAcceptedLegalDocuments) {
      this.navigateToRedirect();
      return;
    }

    this.syncLegalAcceptance();
  }

  public get submitDisabled(): boolean {
    return (
      this.legalForm.invalid || this.versionsUnavailable || this.submitting
    );
  }

  public onSubmit(): void {
    const legalVersions = this.getLegalVersionsForAcceptance();
    if (this.legalForm.invalid || !legalVersions) {
      this.error = {
        error: {
          error: legalVersions
            ? "LEGAL_ACCEPTANCE_REQUIRED"
            : "LEGAL_VERSIONS_UNAVAILABLE",
        },
      };
      return;
    }

    this.submitting = true;
    this.setSubmitText("FINALIZING_SIGNUP");

    const acceptSubscription = this.authService
      .acceptLegalDocuments(
        legalVersions.termsVersion,
        legalVersions.privacyVersion
      )
      .subscribe({
        next: () => {
          this.navigateToRedirect();
        },
        error: (error: CustomError) => {
          this.error = error;
          this.submitting = false;
          this.setSubmitText("FINALIZE_SIGNUP_CTA");
          this.cdr.markForCheck();
        },
      });
    this.subscriptions.push(acceptSubscription);
  }

  private syncLegalAcceptance(): void {
    const legalAcceptanceSubscription = this.authService
      .syncLegalAcceptance()
      .subscribe((hasAcceptedLegalDocuments) => {
        if (hasAcceptedLegalDocuments) {
          this.navigateToRedirect();
          return;
        }

        this.initializeLegalCompletion();
      });
    this.subscriptions.push(legalAcceptanceSubscription);
  }

  private initializeLegalCompletion(): void {
    this.translate.defaultLang = this.userLanguage;
    this.translate.reloadLang(this.userLanguage);
    this.translate.use(this.userLanguage);
    this.setSubmitText("FINALIZE_SIGNUP_CTA");
    this.loadCurrentLegalVersions();

    const translateSubscription = this.translate.onLangChange.subscribe(() => {
      this.setSubmitText("FINALIZE_SIGNUP_CTA");
      this.cdr.markForCheck();
    });
    this.subscriptions.push(translateSubscription);
  }

  private loadCurrentLegalVersions(): void {
    this.versionsUnavailable = true;
    const legalVersionsSubscription = this.legalService
      .getCurrentVersions()
      .subscribe({
        next: (versions) => {
          this.currentLegalVersions = versions;
          this.versionsUnavailable = false;
          this.cdr.markForCheck();
        },
        error: () => {
          this.error = {
            error: {
              error: "LEGAL_VERSIONS_UNAVAILABLE",
            },
          };
          this.versionsUnavailable = true;
          this.cdr.markForCheck();
        },
      });
    this.subscriptions.push(legalVersionsSubscription);
  }

  private setSubmitText(key: string): void {
    const translateSubscription = this.translate
      .get(key)
      .subscribe((translatedText) => {
        this.submitButtonText = translatedText;
        this.cdr.markForCheck();
      });
    this.subscriptions.push(translateSubscription);
  }

  private getLegalVersionsForAcceptance(): {
    termsVersion: string;
    privacyVersion: string;
  } | null {
    if (!this.currentLegalVersions || this.versionsUnavailable) {
      return null;
    }

    return {
      termsVersion: this.currentLegalVersions.terms.version,
      privacyVersion: this.currentLegalVersions.privacy.version,
    };
  }

  private navigateToRedirect(): void {
    this.router.navigateByUrl(this.getRedirectUrl()).catch(() => undefined);
  }

  private getRedirectUrl(): string {
    const redirectTo = this.route.snapshot.queryParamMap.get("redirectTo");

    if (
      !redirectTo?.startsWith("/") ||
      redirectTo.startsWith("//") ||
      redirectTo.startsWith("/complete-signup")
    ) {
      return "/";
    }

    return redirectTo;
  }

  ngOnDestroy(): void {
    this.renderer.removeClass(document.body, "arts-bg");
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }
}
